import { supabase } from '@/shared/lib/supabase/client'

// Web push via Firebase Cloud Messaging. Tokens are stored as multi-device rows in
// connector_push_subscriptions so a browser and an Android device coexist for one
// connector. Firebase is imported dynamically so it stays out of the main bundle for the
// majority of sessions that never enable push.

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined
const FIREBASE_CONFIG = import.meta.env.VITE_FIREBASE_CONFIG as string | undefined

// FCM looks for this exact filename at the origin root unless a registration is passed in.
const SW_PATH = '/firebase-messaging-sw.js'

export type PushSupport =
  | 'ready'
  | 'not-configured'
  | 'unsupported'
  | 'denied'
  | 'granted'
  | 'insecure'

export function pushConfigured(): boolean {
  return Boolean(VAPID_KEY && FIREBASE_CONFIG)
}

// Reported to the UI so the control can explain the situation instead of offering a button
// that cannot work. Browser permission cannot be re-requested once denied, so that case
// needs different copy pointing at site settings.
export function pushSupport(): PushSupport {
  if (!pushConfigured()) return 'not-configured'
  if (typeof window === 'undefined') return 'unsupported'
  if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
    return 'unsupported'
  }
  // Service workers require a secure context. localhost counts as secure.
  if (!window.isSecureContext) return 'insecure'
  if (Notification.permission === 'denied') return 'denied'
  if (Notification.permission === 'granted') return 'granted'
  return 'ready'
}

export async function registerWebPush(): Promise<{ ok: boolean; reason?: string }> {
  const support = pushSupport()
  if (support === 'not-configured') return { ok: false, reason: 'Push is not configured yet' }
  if (support === 'unsupported') return { ok: false, reason: 'This browser cannot receive push' }
  if (support === 'insecure') return { ok: false, reason: 'Push needs a secure (https) connection' }
  if (support === 'denied') {
    return { ok: false, reason: 'Notifications are blocked in your browser settings' }
  }

  try {
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') return { ok: false, reason: 'Permission was not granted' }

    // Registered explicitly and passed to getToken, so FCM uses this worker rather than
    // racing the PWA service worker that vite-plugin-pwa also installs. The config rides on
    // the query string because a static worker cannot read import.meta.env.
    const registration = await navigator.serviceWorker.register(
      `${SW_PATH}?config=${encodeURIComponent(FIREBASE_CONFIG as string)}`
    )

    const { initializeApp, getApps } = await import('firebase/app')
    const { getMessaging, getToken } = await import('firebase/messaging')

    const config = JSON.parse(FIREBASE_CONFIG as string)
    const app = getApps().length > 0 ? getApps()[0]! : initializeApp(config)
    const messaging = getMessaging(app)

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY as string,
      serviceWorkerRegistration: registration,
    })

    if (!token) return { ok: false, reason: 'Could not obtain a push token' }

    const { error } = await supabase.rpc('register_push_subscription', {
      p_token: token,
      p_platform: 'web',
      p_user_agent: navigator.userAgent,
    })

    if (error) return { ok: false, reason: error.message }

    localStorage.setItem('psn.push.token', token)
    return { ok: true }
  } catch (e) {
    return { ok: false, reason: e instanceof Error ? e.message : 'Registration failed' }
  }
}

export async function revokeWebPush(): Promise<{ ok: boolean; reason?: string }> {
  const token = localStorage.getItem('psn.push.token')
  if (!token) return { ok: false, reason: 'This device is not registered' }

  try {
    const { error } = await supabase.rpc('revoke_push_subscription', { p_token: token })
    if (error) return { ok: false, reason: error.message }
    localStorage.removeItem('psn.push.token')
    return { ok: true }
  } catch (e) {
    return { ok: false, reason: e instanceof Error ? e.message : 'Could not turn push off' }
  }
}

export function pushEnabledOnThisDevice(): boolean {
  return (
    pushConfigured() &&
    typeof window !== 'undefined' &&
    'Notification' in window &&
    Notification.permission === 'granted' &&
    Boolean(localStorage.getItem('psn.push.token'))
  )
}

// FCM splits delivery: the service worker only sees messages that arrive while no page is
// in the foreground. A focused tab gets them here instead, and without this listener a
// notification received while the app is open would be silently dropped.
export async function listenForForegroundPush(
  onReceived: (payload: { title: string; body: string | null; linkPath: string | null }) => void
): Promise<() => void> {
  if (!pushEnabledOnThisDevice()) return () => {}

  try {
    const { initializeApp, getApps } = await import('firebase/app')
    const { getMessaging, onMessage } = await import('firebase/messaging')

    const config = JSON.parse(FIREBASE_CONFIG as string)
    const app = getApps().length > 0 ? getApps()[0]! : initializeApp(config)
    const messaging = getMessaging(app)

    return onMessage(messaging, (message) => {
      onReceived({
        title: message.notification?.title ?? 'PSN Connect',
        body: message.notification?.body ?? null,
        linkPath: (message.data?.link_path as string | undefined) ?? null,
      })
    })
  } catch {
    return () => {}
  }
}
